import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';

import { adminAPI } from '../../services/api';
import { usePermissions } from '../../context/PermissionsContext';

import {
  TrendingUp, TrendingDown, Edit2, X, Save,
  AlertTriangle, DollarSign, BarChart3
} from 'lucide-react';

const AdminPositions = () => {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPosition, setEditingPosition] = useState(null);
  const [filter, setFilter] = useState('open');

  useEffect(() => {
    if (isSuperAdmin || hasPermission('positions', 'view')) {
      loadPositions();
    }
  }, [filter, isSuperAdmin]);

  const loadPositions = async () => {
    try {
      const params = {};
      if (filter) params.status_filter = filter;
      const res = await adminAPI.getTrades(params);
      setPositions(res.data || []);
    } catch (err) {
      console.error('Failed to load positions');
    } finally {
      setLoading(false);
    }
  };

  // Access control - must be after all hooks
  if (!isSuperAdmin && !hasPermission('positions', 'view')) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: COLORS.textSecondary 
      }}>
        <TrendingUp size={64} style={{ marginBottom: '16px', opacity: 0.5 }} />
        <h2 style={{ color: COLORS.text, marginBottom: '8px' }}>Доступ запрещён</h2>
        <p>У вас нет прав для просмотра позиций</p>
      </div>
    );
  }

  const updatePosition = async (positionId, updates) => {
    try {
      await adminAPI.updatePosition(positionId, updates);
      loadPositions();
      setEditingPosition(null);
    } catch (err) {
      console.error('Failed to update position');
    }
  };

  const closePosition = async (positionId, closeType) => {
    if (!window.confirm(`Закрыть позицию по ${closeType === 'tp' ? 'Take Profit' : closeType === 'sl' ? 'Stop Loss' : 'рынку'}?`)) return;
    try {
      await adminAPI.closePosition(positionId, closeType);
      loadPositions();
    } catch (err) {
      console.error('Failed to close position');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700' }}>
          Позиции
        </h1>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {['open', 'closed', 'pending'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '8px 16px',
                backgroundColor: filter === f ? COLORS.primary : COLORS.card,
                color: filter === f ? '#000' : COLORS.textSecondary,
                border: filter === f ? 'none' : `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {f === 'open' ? 'Открытые' : f === 'closed' ? 'Закрытые' : 'Ожидают'}
            </button>
          ))}
        </div>
      </div>

      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1.2fr 0.8fr 1fr 1fr 1.2fr 0.8fr 100px',
          padding: '14px 20px',
          backgroundColor: COLORS.background,
          borderBottom: `1px solid ${COLORS.border}`,
          gap: '12px',
        }}>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>КЛИЕНТ / ПАРА</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>НАПРАВЛЕНИЕ</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ВХОД</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ТЕКУЩАЯ</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ОБЪЁМ</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>PNL</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>TP / SL</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600', textAlign: 'center' }}>ДЕЙСТВИЯ</span>
        </div>

        {/* Body */}
        <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>Загрузка...</div>
          ) : positions.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>Нет позиций</div>
          ) : (
            positions.map((pos) => {
              const pnlValue = pos.pnl ?? 0;
              const pnlColor = pnlValue >= 0 ? COLORS.success : COLORS.danger;
              
              return (
                <div
                  key={pos.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.5fr 1.2fr 0.8fr 1fr 1fr 1.2fr 0.8fr 100px',
                    padding: '14px 20px',
                    borderBottom: `1px solid ${COLORS.border}20`,
                    gap: '12px',
                    alignItems: 'center',
                  }}
                >
                  {/* Client & Symbol */}
                  <div>
                    <div style={{ color: COLORS.text, fontWeight: '600', fontSize: '14px' }}>{pos.symbol}</div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      {pos.user_email || pos.user_name || pos.user_id?.slice(0, 8) + '...'}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>{pos.leverage}x • {pos.type}</div>
                  </div>

                  {/* Side */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: pos.side === 'long' ? COLORS.success : COLORS.danger,
                  }}>
                    {pos.side === 'long' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    <span style={{ fontSize: '13px', fontWeight: '600' }}>{pos.side?.toUpperCase()}</span>
                  </div>

                  {/* Entry Price */}
                  <div style={{ color: COLORS.text, fontSize: '13px', fontFamily: 'monospace' }}>
                    ${pos.entry_price?.toFixed(2)}
                  </div>

                  {/* Current Price */}
                  <div style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                    {pos.status === 'open' && pos.current_price ? (
                      <span style={{ color: COLORS.primary }}>${pos.current_price?.toFixed(2)}</span>
                    ) : (
                      <span style={{ color: COLORS.textSecondary }}>
                        {pos.close_price ? `$${pos.close_price?.toFixed(2)}` : '—'}
                      </span>
                    )}
                  </div>

                  {/* Quantity */}
                  <div style={{ color: COLORS.text, fontSize: '13px' }}>
                    {pos.quantity} ({pos.margin?.toFixed(2)}$)
                  </div>

                  {/* PnL */}
                  <div>
                    <div style={{
                      color: pnlColor,
                      fontSize: '15px',
                      fontWeight: '700',
                    }}>
                      {pnlValue >= 0 ? '+' : ''}{pnlValue.toFixed(2)}$
                    </div>
                    {pos.status === 'open' && pos.current_price && (
                      <div style={{ color: COLORS.textSecondary, fontSize: '10px' }}>
                        Real-time
                      </div>
                    )}
                  </div>

                  {/* TP/SL */}
                  <div style={{ fontSize: '12px' }}>
                    <span style={{ color: COLORS.success }}>{pos.take_profit || '-'}</span>
                    <span style={{ color: COLORS.textSecondary }}> / </span>
                    <span style={{ color: COLORS.danger }}>{pos.stop_loss || '-'}</span>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                    {pos.status === 'open' && (
                      <>
                        {(isSuperAdmin || hasPermission('positions', 'edit')) && (
                          <button
                            onClick={() => setEditingPosition(pos)}
                            style={{
                              padding: '6px',
                              backgroundColor: `${COLORS.primary}15`,
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                            title="Редактировать"
                          >
                            <Edit2 size={14} color={COLORS.primary} />
                          </button>
                        )}
                        {(isSuperAdmin || hasPermission('positions', 'close')) && (
                          <button
                            onClick={() => closePosition(pos.id, 'manual')}
                            style={{
                              padding: '6px',
                              backgroundColor: `${COLORS.danger}15`,
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                            }}
                            title="Закрыть"
                          >
                            <X size={14} color={COLORS.danger} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Edit Position Modal */}
      {editingPosition && (
        <EditPositionModal
          position={editingPosition}
          onClose={() => setEditingPosition(null)}
          onSave={updatePosition}
          onClosePosition={closePosition}
        />
      )}
    </div>
  );
};

const EditPositionModal = ({ position, onClose, onSave, onClosePosition }) => {
  const [form, setForm] = useState({
    entry_price: position.entry_price,
    quantity: position.quantity,
    side: position.side,
    leverage: position.leverage,
    stop_loss: position.stop_loss || '',
    take_profit: position.take_profit || '',
    pnl: position.pnl || 0,
  });

  const handleSave = () => {
    const updates = {};
    if (form.entry_price !== position.entry_price) updates.entry_price = parseFloat(form.entry_price);
    if (form.quantity !== position.quantity) updates.quantity = parseFloat(form.quantity);
    if (form.side !== position.side) updates.side = form.side;
    if (form.leverage !== position.leverage) updates.leverage = parseInt(form.leverage);
    if (form.stop_loss !== (position.stop_loss || '')) updates.stop_loss = form.stop_loss ? parseFloat(form.stop_loss) : null;
    if (form.take_profit !== (position.take_profit || '')) updates.take_profit = form.take_profit ? parseFloat(form.take_profit) : null;
    if (form.pnl !== position.pnl) updates.pnl = parseFloat(form.pnl);
    
    if (Object.keys(updates).length > 0) {
      onSave(position.id, updates);
    } else {
      onClose();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }} onClick={onClose}>
      <div
        style={{
          backgroundColor: COLORS.card,
          borderRadius: '16px',
          padding: '24px',
          width: '100%',
          maxWidth: '500px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
            Редактировать {position.symbol}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Цена входа</label>
            <input
              type="number"
              step="0.01"
              value={form.entry_price}
              onChange={(e) => setForm({ ...form, entry_price: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Объём</label>
            <input
              type="number"
              step="0.001"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Направление</label>
            <select
              value={form.side}
              onChange={(e) => setForm({ ...form, side: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
              }}
            >
              <option value="long">LONG</option>
              <option value="short">SHORT</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Плечо</label>
            <input
              type="number"
              value={form.leverage}
              onChange={(e) => setForm({ ...form, leverage: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Take Profit</label>
            <input
              type="number"
              step="0.01"
              value={form.take_profit}
              onChange={(e) => setForm({ ...form, take_profit: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>Stop Loss</label>
            <input
              type="number"
              step="0.01"
              value={form.stop_loss}
              onChange={(e) => setForm({ ...form, stop_loss: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: COLORS.text,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>PnL ($)</label>
            <input
              type="number"
              step="0.01"
              value={form.pnl}
              onChange={(e) => setForm({ ...form, pnl: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: COLORS.background,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                color: form.pnl >= 0 ? COLORS.success : COLORS.danger,
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
          <button
            onClick={() => onClosePosition(position.id, 'tp')}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: `${COLORS.success}15`,
              border: `1px solid ${COLORS.success}`,
              borderRadius: '8px',
              color: COLORS.success,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Закрыть по TP
          </button>
          <button
            onClick={() => onClosePosition(position.id, 'sl')}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: `${COLORS.danger}15`,
              border: `1px solid ${COLORS.danger}`,
              borderRadius: '8px',
              color: COLORS.danger,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Закрыть по SL
          </button>
        </div>

        <button
          onClick={handleSave}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: COLORS.primary,
            border: 'none',
            borderRadius: '8px',
            color: '#000',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            marginTop: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Save size={18} />
          Сохранить изменения
        </button>
      </div>
    </div>
  );
};

export default AdminPositions;
